/**
 * Authentication Context
 * Provides global authentication state and methods throughout the app
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { InteractionStatus, type AuthenticationResult } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { apiService } from '@/services/apiService';
import { AuthUser, AuthContextType } from '@/types/auth';
import { beginAutoSsoAttempt, clearAutoSsoAttempt } from '@/config/auto-sso-attempt.mjs';
import {
  loginRequest,
  isLocalDevAuthBypassEnabled,
  isSSOConfigured,
} from '@/config/msalConfig';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { instance, inProgress } = useMsal();

  // On mount, check if user is already authenticated. The callback route owns
  // redirect processing and must not compete with a second MSAL interaction.
  useEffect(() => {
    if (isLocalDevAuthBypassEnabled()) {
      refreshLocalDevelopmentAuth();
      return;
    }

    const path = window.location.pathname;
    if (path === '/login' || path === '/auth/callback') {
      setIsLoading(false);
      return;
    }
    if (inProgress !== InteractionStatus.None) return;
    void refreshAuth();
  }, [inProgress]);

  const refreshLocalDevelopmentAuth = async () => {
    try {
      setIsLoading(true);
      const currentUser = await apiService.loginWithLocalDevBypass();
      setUser(currentUser);
    } catch (error) {
      console.error('Local development authentication bypass failed:', error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshAuth = async () => {
    try {
      setIsLoading(true);
      const currentUser = await apiService.getCurrentUser();
      clearAutoSsoAttempt(sessionStorage);
      setUser(currentUser);
    } catch {
      // No app session — try cached MSAL state first, then make one guarded
      // top-level redirect so Entra can reuse its browser session reliably.
      if (isSSOConfigured()) {
        const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
        let result: AuthenticationResult | undefined;
        try {
          result = account
            ? await instance.acquireTokenSilent({ ...loginRequest, account })
            : await instance.ssoSilent(loginRequest);
        } catch {
          // Continue to the reliable top-level redirect below.
        }

        if (result?.idToken) {
          try {
            const loggedInUser = await apiService.loginWithSSO(result.idToken);
            clearAutoSsoAttempt(sessionStorage);
            setUser(loggedInUser);
            return;
          } catch {
            setUser(null);
            return;
          }
        }

        if (beginAutoSsoAttempt(sessionStorage)) {
          try {
            await instance.loginRedirect({
              ...loginRequest,
              ...(account?.username ? { loginHint: account.username } : {}),
            });
            return;
          } catch {
            // The login page remains the manual fallback.
          }
        }
      }
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const user = await apiService.login(email, password);
      clearAutoSsoAttempt(sessionStorage);
      setUser(user);
    } catch (error) {
      // Re-throw error to be handled by the login component
      throw error;
    }
  };

  const loginWithSSO = async (idToken: string) => {
    try {
      const user = await apiService.loginWithSSO(idToken);
      clearAutoSsoAttempt(sessionStorage);
      setUser(user);
    } catch (error) {
      // Re-throw error to be handled by the login component
      throw error;
    }
  };

  const logout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
    }
  };

  const changePassword = async (newPassword: string) => {
    try {
      await apiService.changePassword(newPassword);
    } catch (error) {
      // Re-throw error to be handled by the password change component
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    loginWithSSO,
    logout,
    refreshAuth,
    changePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
